import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    mainSelectData,
    MapSettingsCreate,
    MapSettingsIndicatorDataRF,
    MapSettingsIndicatorType,
} from "../../services/models/map_settings";
import {
    useCreateMapSettings,
    useFetchMapSectorsInfo,
    useFetchMapSettings,
    useUpdateMapSettings,
} from "../../hooks/useQuery";
import { Spinner } from "react-bootstrap";
import { MainFilter } from "../shared/MainFilter";
import { MapOfSubjectsWrapper } from "../AdminComponents/map-of-subjects/MapOfSubjectsWrapper";
import { Administrator } from "../../services/models/administrator";
import { SubUser, isMinzdravUser } from "../../services/models/sub_user";

interface MapFilterProps {
    isAdministrator: boolean;
    user: Administrator | SubUser | null;
}

export const MapFilter: React.FC<MapFilterProps> = ({ isAdministrator, user }) => {
    const isMinzdrav = user && isMinzdravUser(user as SubUser);
    const [indicatorType, setIndicatorType] = useState<MapSettingsIndicatorType | null>(isMinzdrav ? "data_rf" : null);
    const [indicatorDataRF, setIndicatorDataRF] = useState<MapSettingsIndicatorDataRF | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [appliedYear, setAppliedYear] = useState<number | null>(null);

    const { mapSettingsData, mapSettingsError, mapSettingsIsLoading } = useFetchMapSettings();
    const { createdMapSettings } = useCreateMapSettings(false);
    const { mapSectorsInfoData, mapSectorsInfoIsLoading, mapSectorsInfoError } = useFetchMapSectorsInfo();
    const { updateMapSettings } = useUpdateMapSettings(false);
    // Эффект для установки дефолтных значений, если mapSettingsData отсутствует
    useEffect(() => {
        if (!mapSettingsIsLoading && !mapSettingsData) {
            const defaultSettings: MapSettingsCreate = {
                indicatorType: "data_user", // TODO: data_rf
                indicatorDataRF: "surgicalBedsForChildren",
                tagId: "",
                moduleId: "",
                valueId: "",
                colors: [],
            };
            createdMapSettings(defaultSettings);
            setIndicatorType(defaultSettings.indicatorType);
            setIndicatorDataRF(defaultSettings.indicatorDataRF);
        }
    }, [mapSettingsData, mapSettingsIsLoading, createdMapSettings]);

    const handleUpdateMapSettings = useCallback(
        (updatedValue: MapSettingsCreate) => {
            updateMapSettings(updatedValue);
            setIndicatorType(updatedValue.indicatorType);
            setIndicatorDataRF(updatedValue.indicatorDataRF);
            setAppliedYear(selectedYear);
        },
        [updateMapSettings, selectedYear],
    );

    const memoizedMapSettingsData = useMemo(() => mapSettingsData, [mapSettingsData]);
    const memoizedMapSectorsInfoData = useMemo(() => mapSectorsInfoData, [mapSectorsInfoData]);
    const memoizedSetIndicatorType = useCallback((type: MapSettingsIndicatorType) => {
        setIndicatorType(type);
    }, []);
    const isLoading = mapSectorsInfoIsLoading || mapSettingsIsLoading;
    const hasError = mapSectorsInfoError || mapSettingsError;
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <p>Ошибка загрузки карты или фильтра. Попробуйте обновить страницу</p>
            </div>
        );
    }

    return (
        <>
            {memoizedMapSettingsData && (
                <>
                    <MainFilter
                        mapSettingsData={memoizedMapSettingsData}
                        updateMapSettings={handleUpdateMapSettings}
                        indicatorType={indicatorType}
                        indicatorDataRF={indicatorDataRF || "surgicalBedsForChildren"}
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        isMinzdravUser={isMinzdrav || false}
                    />
                    <MapOfSubjectsWrapper
                        isAdministrator={isAdministrator}
                        mapSettingsData={memoizedMapSettingsData}
                        mapSectorsInfoData={memoizedMapSectorsInfoData}
                        setIndicatorType={memoizedSetIndicatorType}
                        indicatorType={indicatorType}
                        title={indicatorType ? mainSelectData[indicatorType] : ""}
                        selectedYear={appliedYear}
                    />
                </>
            )}
            {(memoizedMapSettingsData === null ||
                memoizedMapSettingsData === undefined ||
                memoizedMapSectorsInfoData === null ||
                memoizedMapSectorsInfoData === undefined) && (
                <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}
        </>
    );
};
